<template>
  <div class="decoration-renderer" :class="{ 'decoration-renderer--preview': showEditorChrome }"
       :data-decoration-scope="scopePageType != null ? scopePageType : undefined">
    <div v-for="node in safeComponents" :key="node.id" class="dr-node" :data-node-id="node.id"
         :data-node-type="node.type" :data-node-label="typeName(node.type)" :style="nodeStyle(node)">

      <!-- 容器：递归渲染 N 列 -->
      <div v-if="isContainer(node)" class="dr-container"
           :style="{ gap: containerGutterPx(node.props) + 'px', background: node.props.bgColor, minHeight: containerMinHeightPx(node.props) + 'px' }">
        <div v-for="(col, ci) in columnsOf(node)" :key="ci" class="dr-col">
          <decoration-renderer :components="col" :scope-page-type="scopePageType" />
        </div>
      </div>

      <!-- 标题文字 -->
      <div v-else-if="node.type === 'title'" class="dr-title"
           :style="titleStyle(node)" @click="goLink(node.props.link)">
        <span :style="titleExStyle(node)">{{ pick(node.props, 'text') }}</span>
      </div>

      <!-- 轮播图 -->
      <el-carousel v-else-if="node.type === 'banner' && bannerVisible(node)" class="dr-banner"
                   :height="bannerHeight(node)"
                   :direction="bannerDirection(node)"
                   :interval="bannerInterval(node)"
                   :indicator-position="bannerIndicatorPosition(node)"
                   :autoplay="bannerAutoplay(node)">
        <el-carousel-item v-for="(it, i) in (node.props.items || [])" :key="i">
          <div class="dr-banner__slide" @click="goLink(it.link)">
            <img class="dr-banner__img" :src="pickItem(it, 'image')" />
            <div v-if="it.overlayBg" class="dr-banner__overlay" :style="{ background: it.overlayBg }"></div>
            <div v-if="pickItem(it, 'title')" class="dr-banner__title" :style="{ color: it.titleColor || '#ffffff' }">{{ pickItem(it, 'title') }}</div>
          </div>
        </el-carousel-item>
      </el-carousel>

      <!-- 单图 -->
      <img v-else-if="node.type === 'imageSingle'" class="dr-img"
           :src="pick(node.props, 'image')"
           :style="{ height: (node.props.height || 300) + 'px', borderRadius: (node.props.radius || 0) + 'px', background: node.props.bgColor }"
           @click="goLink(node.props.link)" />

      <!-- 多图 -->
      <div v-else-if="node.type === 'imageMulti'" class="dr-multi" :style="multiContainerStyle(node)">
        <img v-for="(it, i) in (node.props.items || [])" :key="i" :src="pickItem(it, 'image')"
             class="dr-multi__img"
             :style="multiImgStyle(node, it)"
             @click="goLink(it.link)" />
        <div v-if="!(node.props.items || []).length && showEditorChrome" class="dr-placeholder">{{ typeName(node.type) }}{{ $t('message.home.addImageHint') }}</div>
      </div>

      <!-- 富文本 -->
      <div v-else-if="node.type === 'richText'" class="dr-richtext" v-html="safeHtml(pick(node.props, 'html'))"></div>

      <!-- 留白 -->
      <div v-else-if="node.type === 'blank'"
           :style="{ height: '100%', width: '100%', background: node.props.bgColor }"></div>

      <!-- 通知公告 -->
      <notice-bar
        v-else-if="node.type === 'notice'"
        :items="noticeItems(node)"
        :icon="pick(node.props, 'noticeIcon')"
        :text-color="(node.props.styleEx && node.props.styleEx.textColor) || '#333333'"
        :interval="noticeInterval(node)"
        :scroll-direction="noticeScrollDirection(node)"
        @click-item="onNoticeItemClick"
      />

      <!-- 视频 -->
      <video v-else-if="node.type === 'video'" class="dr-video" :src="node.props.src"
             :autoplay="node.props.autoplay" :loop="node.props.loop" :muted="node.props.muted"
             :controls="node.props.controls"></video>

      <!-- 搜索导航 -->
      <div v-else-if="node.type === 'searchNav'">
        <div class="dr-searchnav"
             :style="{ background: node.props.bgColor, padding: (node.props.paddingV || 8) + 'px ' + (node.props.paddingH || 14) + 'px' }">
          <span class="dr-searchnav__logo">{{ pick(node.props, 'logoName') }}</span>
          <div class="dr-searchnav__box" :style="{ borderRadius: (node.props.searchRadius || 8) + 'px' }">
            <input
              class="dr-searchnav__input"
              type="text"
              :value="searchKeywordOf(node)"
              :placeholder="tProp(pick(node.props, 'placeholder'))"
              @input="onSearchKeywordInput(node, $event)"
              @keyup.enter="submitSearchNav(node)"
            />
            <img v-if="node.props.searchBtnIcon" class="dr-search__btn-img" :src="node.props.searchBtnIcon" alt=""
                 @click.stop="submitSearchNav(node)" />
            <span v-else class="dr-search__btn" @click.stop="submitSearchNav(node)">🔍</span>
          </div>
          <template v-if="node.props.showCart !== 'hide'">
            <img v-if="node.props.cartIcon" class="dr-searchnav__cart-img" :src="node.props.cartIcon" alt=""
                 @click.stop="goCart" />
            <span v-else class="dr-searchnav__cart" @click.stop="goCart">🛒</span>
          </template>
        </div>
        <div v-if="showNodeHotSearch(node)" class="dr-hotsearch">
          <span class="dr-hotsearch__label">{{ $t('message.home.hotSearch') }}</span>
          <span v-for="(w, i) in hotSearchWords" :key="i" class="dr-hotsearch__word" @click.stop="goSearchGoods(w.keyword)">{{ w.keyword }}</span>
        </div>
      </div>

      <!-- 搜索框 -->
      <div v-else-if="node.type === 'search'">
        <div class="dr-search"
             :style="{ background: node.props.bgColor, padding: (node.props.paddingV || 8) + 'px ' + (node.props.paddingH || 14) + 'px' }">
          <div class="dr-search__box"
               :style="{ background: node.props.boxColor, borderRadius: (node.props.radius || 38) + 'px', textAlign: node.props.align }">
            <input
              class="dr-search__input"
              type="text"
              :value="searchKeywordOf(node)"
              :placeholder="tProp(pick(node.props, 'placeholder'))"
              @input="onSearchKeywordInput(node, $event)"
              @keyup.enter="submitSearchNav(node)"
            />
            <img v-if="node.props.searchBtnIcon" class="dr-search__btn-img" :src="node.props.searchBtnIcon" alt=""
                 @click.stop="submitSearchNav(node)" />
            <span v-else class="dr-search__btn" @click.stop="submitSearchNav(node)">🔍</span>
          </div>
        </div>
        <div v-if="showNodeHotSearch(node)" class="dr-hotsearch">
          <span class="dr-hotsearch__label">{{ $t('message.home.hotSearch') }}</span>
          <span v-for="(w, i) in hotSearchWords" :key="i" class="dr-hotsearch__word" @click.stop="goSearchGoods(w.keyword)">{{ w.keyword }}</span>
        </div>
      </div>

      <!-- 导航按钮组宫格 -->
      <div v-else-if="node.type === 'navIconRow'" class="dr-iconrow"
           :style="{ gridTemplateColumns: 'repeat(' + (node.props.perRow || 5) + ',1fr)' }">
        <div v-for="(it, i) in (node.props.items || [])" :key="i" class="dr-iconrow__item"
             :style="{ background: it.bgColor || undefined }" @click="goLink(it.link)">
          <img :src="pickItem(it, 'image')" :style="navIconImgStyle(node, it)" />
          <span v-if="!(node.props.styleEx && node.props.styleEx.showText === 'hide')"
                :style="navIconTextStyle(node, it)">
            {{ pickItem(it, 'name') }}
          </span>
        </div>
      </div>

      <!-- 首页分类导航 -->
      <div v-else-if="node.type === 'categoryNav'" class="dr-catnav"
           :ref="'catnav_' + node.id"
           :class="'dr-catnav--' + catNavCfg(node).dir" :style="catNavWrapStyle(node)"
           @mouseenter="pauseCatNav(node)" @mouseleave="resumeCatNav(node)">
        <div class="dr-catnav__inner" :style="catNavInnerStyle(node)">
          <div v-for="(it, i) in catNavItems(node)" :key="i"
               class="dr-catnav__item" :class="'dr-catnav__item--' + catNavCfg(node).imgLayout"
               :style="catNavItemStyle(node, it)"
               @click="goLink(it.link)">
            <img v-if="catNavCfg(node).showImg && it.icon" class="dr-catnav__icon" :src="it.icon"
                 :style="catNavIconStyle(node)" />
            <span class="dr-catnav__name" :style="catNavTextStyle(node)">{{ pickItem(it, 'name') }}</span>
          </div>
        </div>
      </div>

      <!-- 商品分类 -->
      <div v-else-if="node.type === 'goodsCategory'" class="dr-goodscat"
           :style="{ background: node.props.bgColor, color: node.props.textColor }">
        <span v-for="(it, i) in (node.props.items || [])" :key="i" class="dr-goodscat__item"
              :style="{ color: it.textColor || undefined, background: it.bgColor || undefined }"
              @click="goLink(it.link)">
          <img v-if="it.icon" class="dr-goodscat__icon" :src="it.icon"
               :style="catIconStyle(node)" />
          <span :style="catTextStyle(node)">{{ pickItem(it, 'name') }}</span>
        </span>
      </div>

      <!-- 滚动图片 -->
      <div v-else-if="node.type === 'imageScroll'" class="dr-imgscroll">
        <img v-for="(it, i) in (node.props.items || [])" :key="i" :src="pickItem(it, 'image')"
             :style="imgScrollItemStyle(node, it)" @click="goLink(it.link)" />
      </div>


      <!-- 商品推荐 / 商品甄选 / 秒杀 / 拼团 -->
      <div v-else-if="goodsTypes.indexOf(node.type) >= 0" class="dr-goods">
        <div v-if="secTitle(node)" class="dr-sec-title">{{ secTitle(node) }}</div>
        <div v-if="node.type === 'seckill'" class="dr-seckill-tabs">
          <div v-for="tab in seckillTabTimes" :key="tab.id"
               :class="['dr-seckill-tab', seckillTabIndex===tab.id?'active':'']"
               @click="switchSeckillTab(tab)">
            <div class="tab-time">{{ tab.time }}</div>
            <div class="tab-name">{{ tab.name }}</div>
          </div>
        </div>
        <div class="dr-goods__grid" :style="goodsGridStyle(node)">
          <div v-for="(g, i) in displayBlockItems(node)" :key="i" class="dr-goods__card"
               @click="goGoodsCard(node, g)">
            <img :src="goodsImg(g, node.type)" class="dr-goods__img" />
            <div class="dr-goods__name">{{ goodsName(g, node.type) }}</div>
            <div class="dr-goods__price">¥{{ goodsPrice(g, node.type) }}</div>
          </div>
          <div v-if="!displayBlockItems(node).length" class="dr-empty">{{ $t('message.home.暂无数据') }}</div>
        </div>
      </div>

      <!-- 优惠券 -->
      <div v-else-if="node.type === 'coupon'" class="dr-coupons">
        <div v-for="(c, i) in (dataMap[node.id] || [])" :key="i" class="dr-coupon"
             :style="{ borderColor: node.props.voucherColor || '#ed1c24' }">
          <div class="dr-coupon__amt" :style="{ color: node.props.voucherColor || '#ed1c24' }">¥{{ c.priceExpression || c.couponPrice || c.amount || '' }}</div>
          <div class="dr-coupon__name">{{ c.name || c.couponName || '优惠券' }}</div>
          <button class="dr-coupon__btn" :style="{ background: node.props.voucherColor || '#ed1c24' }" @click.stop="claimCoupon(c)">{{ $t('message.home.领取') }}</button>
        </div>
        <div v-if="!(dataMap[node.id] || []).length" class="dr-empty">{{ $t('message.home.暂无优惠券') }}</div>
      </div>

      <!-- 品牌 -->
      <div v-else-if="node.type === 'brand'" class="dr-brands">
        <div v-if="secTitle(node)" class="dr-sec-title" :style="{ color: (node.props.styleEx && node.props.styleEx.titleColor) || undefined }">{{ secTitle(node) }}</div>
        <div class="dr-brands__grid">
          <img v-for="(b, i) in displayBlockItems(node)" :key="i"
               class="dr-brand__logo" :class="{ 'dr-brand__logo--bordered': brandShowBorder(node) }"
               :style="gridItemStyle(node)"
               :src="b.logo || b.image || b.thumbnail" @click="goBrand(b)" />
        </div>
        <div v-if="!displayBlockItems(node).length" class="dr-empty">{{ $t('message.home.暂无品牌') }}</div>
      </div>

      <!-- 店铺推荐 -->
      <div v-else-if="node.type === 'storeRecommend'" class="dr-stores">
        <div v-if="secTitle(node)" class="dr-sec-title" :style="{ color: (node.props.styleEx && node.props.styleEx.titleColor) || undefined }">{{ secTitle(node) }}</div>
        <div class="dr-stores__grid">
          <div v-for="(s, i) in displayBlockItems(node)" :key="i" class="dr-store" :style="gridItemStyle(node)" @click="goStore(s)">
            <img :src="s.logo || s.image" class="dr-store__logo" />
            <span>{{ s.name || s.storeName }}</span>
          </div>
        </div>
        <div v-if="!displayBlockItems(node).length" class="dr-empty">{{ $t('message.home.暂无店铺') }}</div>
      </div>

      <!-- 热选爆款（props 驱动） -->
      <div v-else-if="node.type === 'hotSelection'" class="dr-hot"
           :style="hotSelectionWrapStyle(node)">
        <div class="dr-hot__grid" :style="hotGridStyle(node)">
          <div v-for="(g, i) in hotDisplayItems(node)" :key="i" class="dr-hot__item"
               :style="{ background: g.bgColor || undefined }" @click="goLink(g.link)">
            <img :src="pickItem(g, 'image')" :style="{ borderRadius: (g.radius || 0) + 'px' }" />
            <span :style="{ color: g.nameColor || undefined }">{{ pickItem(g, 'name') }}</span>
          </div>
        </div>
      </div>

      <!-- Tab 切换（容器） -->
      <div v-else-if="node.type === 'tabs'" :class="['dr-tabs', 'dr-tabs--' + tabStyleKind(node)]">
        <div class="dr-tabs__bar">
          <span v-for="(t, ti) in (node.props.tabs || [])" :key="ti"
                :class="['dr-tabs__label', { 'is-active': tabIdx(node) === ti }]"
                :style="tabLabelStyle(node, t, tabIdx(node) === ti)"
                @click="setTab(node, ti)">
            {{ pickItem(t, 'name') }}<span v-if="pickItem(t, 'badge')" class="dr-tabs__badge" :style="{ background: t.badgeColor || undefined }">{{ pickItem(t, 'badge') }}</span>
          </span>
        </div>
        <decoration-renderer :components="(node.children && node.children[tabIdx(node)]) || []" :scope-page-type="scopePageType" />
      </div>

      <!-- 页脚链接组 -->
      <div v-else-if="node.type === 'footerLinkGroup'" class="dr-fglinks">
        <div class="dr-fglinks__title" :style="{ color: node.props.titleColor }">
          {{ pick(node.props, 'title') }}
        </div>
        <a v-for="(l, i) in (node.props.links || [])" :key="i"
           class="dr-fglinks__item" :style="{ color: l.color || node.props.linkColor }"
           @click.prevent="goLink(l.link)">
          {{ pickItem(l, 'label') }}
        </a>
      </div>

      <!-- 合作伙伴 logo 排 -->
      <div v-else-if="node.type === 'partnerLogoRow'" class="dr-partners">
        <a v-for="(l, i) in (node.props.logos || [])" :key="i"
           class="dr-partner" :title="l.name"
           @click.prevent="goLink(l.link)">
          <img v-if="l.image" :src="l.image"
               :style="{ height: (l.height > 0 ? l.height : (node.props.logoHeight || 30)) + 'px', borderRadius: (l.radius || 0) + 'px' }" />
        </a>
      </div>

      <!-- ICP 备案 -->
      <div v-else-if="node.type === 'icpInfo'" class="dr-icp" :style="{ color: node.props.color }">
        <a v-if="node.props.link && node.props.link.value" :href="node.props.link.value"
           target="_blank" rel="noopener" :style="{ color: node.props.color }">
          {{ pick(node.props, 'text') }}
        </a>
        <span v-else>{{ pick(node.props, 'text') }}</span>
      </div>

      <!-- 顶部导航菜单 -->
      <div v-else-if="node.type === 'topNavMenu'" class="dr-topnav"
           :style="{ gap: (node.props.spacing || 24) + 'px' }">
        <a v-for="(m, i) in (node.props.items || [])" :key="i"
           class="dr-topnav__item"
           :style="{ color: m.color || node.props.textColor, fontWeight: m.bold ? 700 : undefined }"
           @click.prevent="goLink(m.link)">
          {{ pickItem(m, 'label') }}
        </a>
      </div>

      <!-- ===== 业务组件（阶段 2）：访问 vuex / 路由 / i18n ===== -->
      <logo-block v-else-if="node.type === 'logoBlock'"
                  :height="node.props.height || 40"
                  :custom-logo="node.props.customLogo || ''"
                  :link="(node.props.link && node.props.link.value) || '/'" />
      <div v-else-if="node.type === 'searchBox'" class="dr-searchbox-wrap">
        <search-box
          :placeholder="tProp(pick(node.props, 'placeholder'))"
          :button-text="tProp(pick(node.props, 'buttonText'))"
          :width="node.props.width || 400" />
        <div v-if="showNodeHotSearch(node)" class="dr-hotsearch" :style="{ maxWidth: (node.props.width || 400) + 'px' }">
          <span class="dr-hotsearch__label">{{ $t('message.home.hotSearch') }}</span>
          <span v-for="(w, i) in hotSearchWords" :key="i" class="dr-hotsearch__word" @click.stop="goSearchGoods(w.keyword)">{{ w.keyword }}</span>
        </div>
      </div>
      <es-lang v-else-if="node.type === 'langSwitcher'" />
      <es-currency v-else-if="node.type === 'currencySwitcher'" />
      <login-block v-else-if="node.type === 'loginBlock'"
                   :show-avatar="node.props.showAvatar !== false" />
      <cart-icon v-else-if="node.type === 'cartIcon'"
                 :link="(node.props.link && node.props.link.value) || '/cart'"
                 :show-count="node.props.showCount !== false" />
      <message-icon v-else-if="node.type === 'messageIcon'"
                    :link="(node.props.link && node.props.link.value) || '/userInfo/my-order'" />

      <!-- 兜底占位 -->
      <div v-else class="dr-placeholder">{{ typeName(node.type) }}</div>

    </div>
  </div>
</template>

<script>
import { recommend, list as goodsList } from "@/api/goods/goods";
// 业务组件 + lang/currency 切换器改为异步加载，减小首屏 bundle（Vue 2 工厂函数语法）
// webpackChunkName 把 7 个业务组件聚合为单个 dr-biz chunk，避免多请求
import { list as seckillList } from "@/api/marketing/seckill_marketing";
import { list as groupList } from "@/api/marketing/group_marketing";
import { findCouponEnable } from "@/api/marketing/coupon";
import { getCoupon } from "@/api/marketing/coupon_code";
import { list as brandList } from "@/api/goods/brand";
import { list as hotSearchList } from "@/api/goods/hot_search";
import { list as storeList } from "@/api/store/store";
import { index as goodsCategoryIndex } from "@/api/goods/goods_category";
import { getDecorationLangCode, pickI18nValue } from "@/util/decorationI18n";
import { decorationTypeName } from './decorationTypeNames';
import NoticeBar from './NoticeBar.vue';

const CONTAINER_TYPES = ['container'];
const GOODS_TYPES = ['goodsRecommend', 'goodsSelection', 'seckill', 'groupBuy'];

const SYSTEM_PAGE_PC = {
  index: '/index', category: '/classification', search: '/searchGoods',
  cart: '/cart', member: '/userInfo/dashboard',
  seckill: '/flash-sale', groupBuy: '/group',
  couponCenter: '/coupon/center', pointMall: '/point/mall',
  pointLottery: '/point/lottery', live: '/live/list',
  myOrder: '/userInfo/my-order', myCoupon: '/userInfo/coupon',
  myFavorite: '/userInfo/collect-goods', myPoint: '/userInfo/point',
  myBalance: '/userInfo/balance', footprint: '/userInfo/footprint',
  memberVip: '/member/center', storeList: '/store/list',
  announcement: '/announcement/list'
};
const SECKILL_HOUR_NAMES = [
  'ZERO', 'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE', 'SIX', 'SEVEN', 'EIGHT', 'NINE',
  'TEN', 'ELEVEN', 'TWELVE', 'THIRTEEN', 'FOURTEEN', 'FIFTEEN', 'SIXTEEN',
  'SEVENTEEN', 'EIGHTEEN', 'NINETEEN', 'TWENTY', 'TWENTY_ONE', 'TWENTY_TWO', 'TWENTY_THREE'
];
function currentSeckillHour() {
  const h = new Date().getHours();
  return SECKILL_HOUR_NAMES[h] || 'ZERO';
}

export default {
  name: "DecorationRenderer",
  components: {
    NoticeBar,
    LogoBlock: () => import(/* webpackChunkName: "dr-biz" */ "./business/LogoBlock.vue"),
    SearchBox: () => import(/* webpackChunkName: "dr-biz" */ "./business/SearchBox.vue"),
    LoginBlock: () => import(/* webpackChunkName: "dr-biz" */ "./business/LoginBlock.vue"),
    CartIcon: () => import(/* webpackChunkName: "dr-biz" */ "./business/CartIcon.vue"),
    MessageIcon: () => import(/* webpackChunkName: "dr-biz" */ "./business/MessageIcon.vue"),
    EsLang: () => import(/* webpackChunkName: "dr-header" */ "@/components/header/lang.vue"),
    EsCurrency: () => import(/* webpackChunkName: "dr-header" */ "@/components/header/currency.vue")
  },
  props: {
    components: { type: Array, default: () => [] },
    /** 装修槽位：0=首页主体 1=分类 3=自定义 4=页头 5=页尾；用于装修器 iframe 区分可选中区域 */
    scopePageType: { type: Number, default: null }
  },
  data() {
    return { dataMap: {}, tabActive: {}, goodsTypes: GOODS_TYPES, editingScopeTick: 0, searchKeywords: {}, seckillTabTimes: [], seckillTabIndex: 1, hotSearchWords: [] };
  },
  computed: {
    safeComponents() {
      return Array.isArray(this.components) ? this.components.filter(n => n && n.id && n.type) : [];
    },
    /** 预览模式：URL 含 previewPageId 时，给空组件加可视占位 + 最小高度，便于装修器 WYSIWYG 看到拖入位置 */
    isPreviewMode() {
      if (typeof window === 'undefined') return false;
      if (/[?&]previewPageId=/.test(window.location.href)) return true;
      if (window.parent && window.parent !== window) return true;
      return false;
    },
    /** 仅当前编辑槽位显示装修器控件（蓝标/虚线）；其它槽位成熟页展示 */
    showEditorChrome() {
      void this.editingScopeTick;
      if (!this.isPreviewMode) return false;
      if (this.scopePageType == null) return true;
      if (typeof document === 'undefined') return true;
      const editing = document.documentElement.getAttribute('data-dr-editing-scope');
      if (editing == null || editing === '') return true;
      return Number(editing) === this.scopePageType;
    },
    decorationLang() {
      return getDecorationLangCode(this.$store);
    }
  },
  mounted() {
    this.buildSeckillTabTimes();
    this.loadHotSearchWords();
    this.setupLazyLoad();
    this.startAllCatNav();
    this._onEditingScope = () => { this.editingScopeTick++ };
    window.addEventListener('decoration-editing-scope', this._onEditingScope);
  },
  beforeDestroy() {
    this.cleanupLazyLoad();
    this.stopAllCatNav();
    if (this._onEditingScope) {
      window.removeEventListener('decoration-editing-scope', this._onEditingScope);
    }
  },
  watch: {
    components() {
      this.cleanupLazyLoad();
      this.stopAllCatNav();
      this.$nextTick(() => { this.setupLazyLoad(); this.startAllCatNav(); });
    }
  },
  methods: {
    /** 数据驱动组件按视口可见性懒加载，减少首屏并发请求；容器子层由其自身渲染器实例负责 */
    setupLazyLoad() {
      this._fetched = {};
      const dataTypes = { goodsRecommend: 1, goodsSelection: 1, seckill: 1, groupBuy: 1, coupon: 1, brand: 1, storeRecommend: 1, categoryNav: 1 };
      const lazyNodes = this.safeComponents.filter(n => dataTypes[n.type]);
      if (!lazyNodes.length) return;
      if (typeof IntersectionObserver === 'undefined') {
        lazyNodes.forEach(n => this.fetchBlockData(n));
        return;
      }
      const byId = {};
      lazyNodes.forEach(n => { byId[n.id] = n; });
      this._byId = byId;
      this._observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          const id = entry.target.getAttribute('data-node-id');
          const node = this._byId && this._byId[id];
          if (node && !this._fetched[id]) {
            this._fetched[id] = true;
            this.fetchBlockData(node);
          }
          this._observer.unobserve(entry.target);
        });
      }, { rootMargin: '200px 0px' });
      this.$nextTick(() => {
        if (!this.$el || !this.$el.children) return;
        const els = this.$el.children;
        for (let i = 0; i < els.length; i++) {
          const el = els[i];
          const id = el.getAttribute && el.getAttribute('data-node-id');
          if (id && byId[id]) this._observer.observe(el);
        }
      });
    },
    cleanupLazyLoad() {
      if (this._observer) { try { this._observer.disconnect(); } catch (e) {} this._observer = null; }
      this._fetched = {};
      this._byId = null;
    },
    /** 单组件数据拉取：按 props.dataSource 分派 auto/category/manual */
    fetchBlockData(node) {
      const t = node.type, id = node.id, props = node.props || {};
      const limit = this.blockCapacity(props) || parseInt(props.limit, 10) || 12;
      if (t === 'goodsRecommend' || t === 'goodsSelection') {
        const ds = props.dataSource;
        // manual：admin 选商品时已存 {id, name, thumbnail, price}，无需再 fetch
        if (ds === 'manual' && Array.isArray(props.goodsIds) && props.goodsIds.length) {
          this.setData(id, props.goodsIds.slice(0, limit));
          return;
        }
        // category：按选中分类拉
        if (ds === 'category' && props.categoryId && props.categoryId.id) {
          const listParams = { goodsCategoryId: props.categoryId.id, pageNumber: 1, pageSize: limit };
          goodsList([], listParams)
            .then(r => {
              const list = this.extractList(r, ['content', 'data']);
              this.setData(id, list);
            }).catch(() => {});
          return;
        }
        // auto
        recommend({ count: limit }).then(r => this.setData(id, this.extractList(r, ['recommendGoodss', 'content']))).catch(() => {});
      } else if (t === 'seckill') {
        const tab = this.seckillTabTimes[this.seckillTabIndex - 1];
        const hour = tab ? tab.hour : currentSeckillHour();
        seckillList({ pageNumber: 1, pageSize: limit, hour }).then(r => this.setData(id, this.extractList(r, ['data', 'content']))).catch(() => {});
      } else if (t === 'groupBuy') {
        const ds = props.dataSource;
        if (ds === 'manual' && Array.isArray(props.goodsIds) && props.goodsIds.length) {
          this.setData(id, props.goodsIds.slice(0, limit));
          return;
        }
        groupList({ pageNumber: 1, pageSize: limit }).then(r => this.setData(id, this.extractList(r, ['content', 'data']))).catch(() => {});
      } else if (t === 'coupon') {
        findCouponEnable({ pageNumber: 1, pageSize: limit, quantity: 0 }).then(r => this.setData(id, this.extractList(r, ['content', 'data']))).catch(() => {});
      } else if (t === 'brand') {
        const ds = props.dataSource;
        const cap = this.blockCapacity(props);
        if (ds === 'manual' && Array.isArray(props.brandIds) && props.brandIds.length) {
          this.setData(id, props.brandIds.slice(0, cap));
          return;
        }
        brandList(1).then(r => this.setData(id, this.extractList(r, ['content', 'data']).slice(0, cap))).catch(() => {});
      } else if (t === 'storeRecommend') {
        const ds = props.dataSource;
        const cap = this.blockCapacity(props);
        if (ds === 'manual' && Array.isArray(props.storeIds) && props.storeIds.length) {
          this.setData(id, props.storeIds.slice(0, cap));
          return;
        }
        storeList({ pageNumber: 1, pageSize: cap }).then(r => this.setData(id, this.extractList(r, ['content', 'data']).slice(0, cap))).catch(() => {});
      } else if (t === 'categoryNav') {
        // 手动配置了导航数据则用手动；否则按 level 自动拉商品分类树并平铺该级
        if (Array.isArray(props.items) && props.items.length) {
          this.setData(id, props.items);
          return;
        }
        goodsCategoryIndex().then(r => {
          const tree = this.extractList(r, ['data', 'content']);
          this.setData(id, this.flattenCategoryByLevel(tree, props.level));
        }).catch(() => {});
      }
    },
    setData(id, list) { this.$set(this.dataMap, id, Array.isArray(list) ? list : []); },
    /** 按级别把分类树拍平：level=2 取所有二级，level=3 取所有三级；映射为导航项 */
    flattenCategoryByLevel(tree, level) {
      let cur = Array.isArray(tree) ? tree : [];
      const depth = Math.max(1, parseInt(level, 10) || 2);
      for (let d = 1; d < depth; d++) {
        const next = [];
        cur.forEach(n => { (n && Array.isArray(n.children) ? n.children : []).forEach(c => next.push(c)); });
        cur = next;
      }
      return cur.map(c => ({
        name: c.name,
        icon: c.image || c.icon || '',
        link: { linkType: 'category', value: c.id }
      }));
    },
    /** 分类导航渲染项：手动配置优先，否则用自动拉取的 dataMap */
    catNavItems(node) {
      const manual = (node.props && node.props.items) || [];
      if (manual.length) return manual;
      return this.dataMap[node.id] || [];
    },
    /** 分类导航布局参数：方向/行数/图片显隐/位置/尺寸/字号/项宽高（PC 默认图片在上） */
    catNavCfg(node) {
      const p = node.props || {}, ex = p.styleEx || {};
      const dir = p.direction === 'vertical' ? 'vertical' : 'horizontal';
      const rows = Math.max(1, parseInt(p.rows, 10) || 1);
      const showImg = (p.showImage || 'show') !== 'hide';
      const imgLayout = p.imageLayout === 'left' ? 'left' : 'top';
      const defImg = imgLayout === 'left' ? 24 : 40;
      const imgW = parseInt(ex.imgWidth, 10) > 0 ? parseInt(ex.imgWidth, 10) : defImg;
      const imgH = parseInt(ex.imgHeight, 10) > 0 ? parseInt(ex.imgHeight, 10) : defImg;
      const fontSize = parseInt(ex.textSize, 10) > 0 ? parseInt(ex.textSize, 10) : 13;
      const radius = parseInt(ex.imgRadius, 10) || 0;
      const itemBg = ex.itemBg || '';
      const textLineH = Math.ceil(fontSize * 1.2);
      const textH = textLineH * 2;
      const textMinW = Math.max(80, Math.ceil(fontSize * 5.5));
      let itemW; let itemH;
      if (imgLayout === 'left') {
        itemW = (showImg ? imgW + 8 : 0) + textMinW;
        itemH = Math.max(showImg ? imgH + 8 : 0, textH) + 8;
      } else {
        itemW = Math.max(imgW + 16, textMinW + 8);
        itemH = (showImg ? imgH + 6 : 0) + textH + 10;
      }
      return { dir, rows, showImg, imgLayout, imgW, imgH, fontSize, radius, itemBg, itemW, itemH };
    },
    catNavWrapStyle(node) {
      const c = this.catNavCfg(node);
      if (c.dir === 'vertical') {
        return { maxHeight: (c.rows * c.itemH) + 'px', overflowY: 'auto', overflowX: 'hidden' };
      }
      return { height: (c.rows * c.itemH) + 'px', overflowX: 'auto', overflowY: 'hidden' };
    },
    catNavInnerStyle(node) {
      const c = this.catNavCfg(node);
      if (c.dir === 'vertical') {
        return { display: 'flex', flexDirection: 'row', flexWrap: 'wrap', width: '100%' };
      }
      const n = (this.catNavItems(node) || []).length;
      const cols = Math.max(1, Math.ceil(n / c.rows));
      return { display: 'flex', flexDirection: 'column', flexWrap: 'wrap', alignContent: 'flex-start',
        flexShrink: 0, height: (c.rows * c.itemH) + 'px', width: (cols * c.itemW) + 'px' };
    },
    catNavItemStyle(node, it) {
      const c = this.catNavCfg(node);
      const isLeft = c.imgLayout === 'left';
      return { width: c.itemW + 'px', height: c.itemH + 'px',
        background: (it && it.bgColor) || c.itemBg || 'transparent',
        color: (it && it.textColor) || undefined,
        display: 'flex',
        flexDirection: isLeft ? 'row' : 'column',
        alignItems: 'center',
        justifyContent: isLeft ? 'flex-start' : 'center',
        textAlign: isLeft ? 'left' : 'center' };
    },
    catNavIconStyle(node) {
      const c = this.catNavCfg(node);
      const base = { width: c.imgW + 'px', height: c.imgH + 'px', borderRadius: c.radius + 'px', flexShrink: 0 };
      if (c.imgLayout === 'left') return Object.assign(base, { marginRight: '6px', marginBottom: 0 });
      return Object.assign(base, { marginBottom: '4px', marginRight: 0 });
    },
    catNavTextStyle(node) {
      const c = this.catNavCfg(node);
      const s = { fontSize: c.fontSize + 'px', lineHeight: 1.2 };
      if (c.imgLayout === 'left') { s.flex = '1'; s.minWidth = '0'; }
      return s;
    },
    catNavDom(node) {
      const ref = this.$refs['catnav_' + node.id];
      return Array.isArray(ref) ? ref[0] : ref;
    },
    /** 启动分类导航自动滚动：横向连续慢速左移；纵向逐行步进上移；拖动/悬停时暂停 */
    startCatNavAuto(node) {
      this.stopCatNavAuto(node);
      if (!this._catnavTimers) this._catnavTimers = {};
      if (!this._catnavPaused) this._catnavPaused = {};
      const id = node.id;
      const dir = this.catNavCfg(node).dir;
      if (dir === 'vertical') {
        this._catnavTimers[id] = setInterval(() => {
          if (this._catnavPaused[id]) return;
          const dom = this.catNavDom(node);
          if (!dom || dom.scrollHeight <= dom.clientHeight + 1) return;
          let next = dom.scrollTop + this.catNavCfg(node).itemH;
          if (next > dom.scrollHeight - dom.clientHeight + 1) next = 0;
          if (dom.scrollTo) dom.scrollTo({ top: next, behavior: 'smooth' });
          else dom.scrollTop = next;
        }, 2500);
      } else {
        this._catnavTimers[id] = setInterval(() => {
          if (this._catnavPaused[id]) return;
          const dom = this.catNavDom(node);
          if (!dom || dom.scrollWidth <= dom.clientWidth + 1) return;
          let next = dom.scrollLeft + 1;
          if (next >= dom.scrollWidth - dom.clientWidth) next = 0;
          dom.scrollLeft = next;
        }, 60);
      }
    },
    stopCatNavAuto(node) {
      if (this._catnavTimers && this._catnavTimers[node.id]) {
        clearInterval(this._catnavTimers[node.id]);
        delete this._catnavTimers[node.id];
      }
    },
    pauseCatNav(node) { if (!this._catnavPaused) this._catnavPaused = {}; this._catnavPaused[node.id] = true; },
    resumeCatNav(node) { if (this._catnavPaused) this._catnavPaused[node.id] = false; },
    startAllCatNav() {
      this.$nextTick(() => {
        this.safeComponents.forEach(n => {
          if (n.type === 'categoryNav') {
            this.startCatNavAuto(n);
          }
        });
      });
    },
    stopAllCatNav() {
      if (!this._catnavTimers) return;
      Object.keys(this._catnavTimers).forEach(k => clearInterval(this._catnavTimers[k]));
      this._catnavTimers = {};
    },
    extractList(r, keys) {
      let d = r && r.data && r.data.data;
      if (Array.isArray(d)) return d;
      if (d && typeof d === 'object') {
        for (let i = 0; i < keys.length; i++) {
          if (Array.isArray(d[keys[i]])) return d[keys[i]];
        }
        if (Array.isArray(d.data)) return d.data;
        if (d.data && Array.isArray(d.data.content)) return d.data.content;
      }
      return [];
    },
    noticeItems(node) {
      return (node.props.items || []).map(it => ({
        ...it,
        name: this.pickItem(it, 'name')
      }));
    },
    noticeInterval(node) {
      const ex = (node.props && node.props.styleEx) || {};
      return ex.interval || 5000;
    },
    noticeScrollDirection(node) {
      const ex = (node.props && node.props.styleEx) || {};
      return ex.scrollDirection || 'vertical';
    },
    onNoticeItemClick(it) {
      if (it && it.link) this.goLink(it.link);
    },
    secTitle(node) {
      if (node.type === 'seckill') {
        if (node.props.showTitle === 'hide') return '';
        return '⚡ ' + (this.pick(node.props, 'titleDesc') || '秒杀');
      }
      if (node.type === 'groupBuy') {
        if (node.props.showTitle === 'hide') return '';
        return this.pick(node.props, 'titleDesc') || '拼团好物，一起省';
      }
      const ex = node.props && node.props.styleEx;
      if (!ex || ex.showTitle === 'hide') return '';
      return this.pick(ex, 'title') || '';
    },
    isMarketingGoods(nodeType) {
      return nodeType === 'seckill' || nodeType === 'groupBuy';
    },
    goodsImg(g, nodeType) {
      const goods = g && g.goods;
      if (goods && this.isMarketingGoods(nodeType)) {
        return goods.thumbnail || goods.image || '';
      }
      return g.thumbnail || (goods && (goods.thumbnail || goods.image)) || g.image || '';
    },
    goodsName(g, nodeType) {
      const goods = g && g.goods;
      if (goods && this.isMarketingGoods(nodeType)) {
        return goods.name || goods.goodsName || '';
      }
      return g.name || g.goodsName || (goods && goods.name) || '';
    },
    goodsPrice(g, nodeType) {
      if (this.isMarketingGoods(nodeType) && g.marketingPrice != null && g.marketingPrice !== '') {
        return g.marketingPrice;
      }
      return g.price || g.marketingPrice || g.seckillPrice || (g.goods && g.goods.price) || '';
    },
    goodsId(g) { return (g.goods && g.goods.id) || g.goodsId || g.id || ''; },
    goGoodsCard(node, g) {
      const gid = this.goodsId(g);
      if (!gid) return;
      const query = { id: gid };
      if (g.marketingId && this.isMarketingGoods(node.type)) {
        query.marketingId = g.marketingId;
        query.marketingModeId = node.type === 'seckill' ? 'seckillMarketing' : 'groupMarketing';
        if (node.type === 'seckill') { query.seckillable = this.seckillTabIndex === 1 ? 'true' : 'false'; }
      }
      this.$router.push({ path: '/productDetails', query });
    },
    //生成4个时段Tab：当前小时 + 后续3小时
    buildSeckillTabTimes() {
      const nowHour = new Date().getHours();
      const tabs = [];
      for (let i = 0; i < 4; i++) {
        const h = (nowHour + i) % 24;
        tabs.push({
          id: i + 1,
          time: h + ':00',
          name: i === 0 ? this.$t('message.home.seckillRushing') : this.$t('message.home.seckillWillStart'),
          hour: SECKILL_HOUR_NAMES[h],
        });
      }
      this.seckillTabTimes = tabs;
    },
    switchSeckillTab(tab) {
      this.seckillTabIndex = tab.id;
      this.safeComponents.filter(n => n.type === 'seckill').forEach(n => this.fetchBlockData(n));
    },
    blockCapacity(props) {
      const legacy = parseInt(props && props.limit, 10);
      if (props && props.perRow == null && props.rows == null && legacy > 0) return legacy;
      const perRow = Math.max(1, parseInt(props && props.perRow, 10) || 4);
      const rows = Math.max(1, parseInt(props && props.rows, 10) || 1);
      return perRow * rows;
    },
    displayBlockItems(node) {
      const list = this.dataMap[node.id] || [];
      return list.slice(0, this.blockCapacity(node.props || {}));
    },
    blockGridStyle(node, fallbackPerRow) {
      const perRow = Math.max(1, parseInt(node.props && node.props.perRow, 10) || fallbackPerRow || 4);
      const gap = Math.max(6, Math.min(14, Math.floor(72 / perRow)));
      return {
        display: 'grid',
        gridTemplateColumns: `repeat(${perRow}, minmax(0, 1fr))`,
        gap: gap + 'px',
        boxSizing: 'border-box'
      };
    },
    goodsGridStyle(node) {
      return Object.assign(this.blockGridStyle(node, 6), { padding: '0 12px 12px' });
    },
    hotGridStyle(node) {
      return Object.assign(this.blockGridStyle(node, 4), { padding: '12px' });
    },
    hotSelectionBg(node) {
      const c = node.props && node.props.styleEx && node.props.styleEx.bgColor;
      return (c != null && c !== '') ? c : '#f5f6fa';
    },
    hotSelectionWrapStyle(node) {
      return { background: this.hotSelectionBg(node), width: '100%' };
    },
    hotDisplayItems(node) {
      return (node.props.groups || []).slice(0, this.blockCapacity(node.props || {}));
    },
    gridItemStyle(node) {
      const perRow = Math.max(1, parseInt(node.props && node.props.perRow) || 4);
      const pct = (100 / perRow).toFixed(4);
      return { width: pct + '%', flexBasis: pct + '%', maxWidth: pct + '%', boxSizing: 'border-box' };
    },
    tabIdx(node) { return this.tabActive[node.id] || 0; },
    setTab(node, i) { this.$set(this.tabActive, node.id, i); },
    isContainer(node) { return CONTAINER_TYPES.indexOf(node.type) >= 0; },
    containerGutterPx(props) {
      const g = parseInt(props && props.gutter, 10);
      return isNaN(g) ? 0 : Math.max(0, g);
    },
    /** 容器最小高度：用户设了用用户值，未设（老数据）走 0 保持原行为，新建默认 schema=100 */
    containerMinHeightPx(props) {
      if (!props || props.minHeight == null || props.minHeight === '') return 0;
      const h = parseInt(props.minHeight, 10);
      return isNaN(h) ? 0 : Math.max(0, h);
    },
    columnsOf(node) {
      const n = Math.max(1, parseInt(node.props && node.props.columns) || 1);
      const ch = Array.isArray(node.children) ? node.children : [];
      const cols = [];
      for (let i = 0; i < n; i++) cols.push(Array.isArray(ch[i]) ? ch[i] : []);
      return cols;
    },
    typeName(t) { return decorationTypeName(t, this.$t.bind(this)); },
    /** 对已知中文默认值尝试 $t() 回退（适用于存储了中文默认的 placeholder 等字段） */
    tProp(v) {
      if (!v) return '';
      const key = 'message.home.' + v;
      const t = this.$t(key);
      return t !== key ? t : v;
    },
    /** 字段级 i18n 取值：xxxI18n[lang] ?? xxx */
    pick(props, key) {
      return pickI18nValue(props, key, this.curLang()) || '';
    },
    pickItem(item, key) {
      return pickI18nValue(item, key, this.curLang()) || '';
    },
    curLang() {
      return this.decorationLang || getDecorationLangCode(this.$store);
    },
    /**
     * 阶段 8 新布局模型：以"居中"为基准的双向偏移 shift (-100 ~ +100)
     *  - widthMode='fixed': 宽度 = width px
     *  - widthMode='percent': 宽度 = (colSpan/24)*100%
     *  - 偏移：offsetShift (-100 ~ +100)，0=居中，±100=完全靠边
     *      margin-left = calc((100% - <width>) * (100 + <shift>) / 200)
     *  - 老 offset 字段双读 fallback：没 offsetShift 时换算 shift=(offset-12)*100/12
     */
    nodeStyle(node) {
      const s = node.style || {};
      const p = s.padding || {}, m = s.margin || {}, r = s.radius || {};
      const layout = s.layout || {};
      const style = {
        background: node.type === 'hotSelection' ? this.hotSelectionBg(node) : (s.background || 'transparent'),
        padding: `${p.t || 0}px ${p.r || 0}px ${p.b || 0}px ${p.l || 0}px`,
        margin: `${m.t || 0}px ${m.r || 0}px ${m.b || 0}px ${m.l || 0}px`,
        borderRadius: `${r.tl || 0}px ${r.tr || 0}px ${r.br || 0}px ${r.bl || 0}px`,
        boxSizing: 'border-box'
      };
      const mode = (layout.widthMode === 'fixed') ? 'fixed' : 'percent';
      // 1) 宽度
      let widthExpr;
      if (mode === 'fixed' && layout.width > 0) {
        widthExpr = layout.width + 'px';
        style.width = widthExpr;
        style.flexBasis = widthExpr;
        style.maxWidth = widthExpr;
      } else {
        const span = Math.max(1, Math.min(24, parseInt(layout.colSpan, 10) || 24));
        const pct = (span / 24) * 100;
        widthExpr = pct + '%';
        if (span < 24) {
          style.flexBasis = `calc(${pct}% - 4px)`;
          style.maxWidth = `calc(${pct}% - 4px)`;
        } else {
          style.flexBasis = '100%';
        }
      }
      // 2) 偏移 shift (-100~+100) ：margin-left = (100% - width) * (100 + shift) / 200
      let shift;
      if (layout.offsetShift !== undefined && layout.offsetShift !== null) {
        shift = Number(layout.offsetShift) || 0;
      } else {
        // 老数据：offset (0-24) → shift = (offset-12)*100/12
        const old = Number(layout.offset);
        shift = isNaN(old) ? 0 : (old - 12) * 100 / 12;
      }
      shift = Math.max(-100, Math.min(100, shift));
      style.marginLeft = `calc((100% - ${widthExpr}) * (100 + ${shift}) / 200)`;
      const appliedH = this.resolveNodeHeight(node, s);
      if (node.type === 'categoryNav') {
        const c = this.catNavCfg(node);
        const autoH = c.rows * c.itemH;
        if (appliedH > 0) style.height = Math.max(appliedH, autoH) + 'px';
        else style.minHeight = autoH + 'px';
      } else if (appliedH > 0) {
        style.height = appliedH + 'px';
      }
      return style;
    },
    /** 节点外框高度：留白用 props.height；容器/Tab 随内容；其余用 style.height */
    resolveNodeHeight(node, s) {
      if (node.type === 'blank') {
        const h = parseInt(node.props && node.props.height, 10);
        return h > 0 ? h : 20;
      }
      if (node.type === 'container' || node.type === 'tabs') return 0;
      const styleH = parseInt(s && s.height, 10);
      return styleH > 0 ? styleH : 0;
    },
    titleStyle(node) {
      const p = node.props || {};
      return {
        textAlign: p.align || 'left',
        fontSize: (p.fontSize || 28) + 'px',
        fontWeight: p.fontWeight || 900,
        color: p.color || '#000',
        lineHeight: (p.lineHeight || 35) + 'px',
        cursor: (p.link && p.link.linkType && p.link.linkType !== 'none') ? 'pointer' : 'default'
      };
    },
    bannerHeight(node) {
      const ex = node.props.styleEx || {};
      return (ex.height || 300) + 'px';
    },
    bannerStyleEx(node) {
      return node.props.styleEx || {};
    },
    bannerVisible(node) {
      return this.bannerStyleEx(node).show !== 'hide';
    },
    bannerDirection(node) {
      return this.bannerStyleEx(node).direction === 'vertical' ? 'vertical' : 'horizontal';
    },
    bannerShowDots(node) {
      return this.bannerStyleEx(node).showDots === 'show';
    },
    bannerAutoplay(node) {
      return this.bannerVisible(node) && (this.bannerStyleEx(node).duration || 0) !== 0;
    },
    bannerInterval(node) {
      const ex = this.bannerStyleEx(node);
      if (ex.duration != null && ex.duration !== '') {
        return Math.max(1, Number(ex.duration) || 3) * 1000;
      }
      return ex.interval || 3000;
    },
    bannerIndicatorPosition(node) {
      if (!this.bannerShowDots(node)) return 'none';
      return this.bannerStyleEx(node).dotStyle === 'bar' ? '' : 'outside';
    },
    multiStyleEx(node) {
      return (node.props && node.props.styleEx) || {};
    },
    titleExStyle(node) {
      const ex = node.props.styleEx || {};
      const p = ex.textPadding || {}, m = ex.textMargin || {};
      const style = {};
      if (ex.letterSpacing) style.letterSpacing = ex.letterSpacing + 'px';
      if (p.t || p.r || p.b || p.l) style.padding = `${p.t||0}px ${p.r||0}px ${p.b||0}px ${p.l||0}px`;
      if (m.t || m.r || m.b || m.l) style.margin = `${m.t||0}px ${m.r||0}px ${m.b||0}px ${m.l||0}px`;
      return style;
    },
    catIconStyle(node) {
      const ex = node.props.styleEx || {};
      const mar = ex.imgMargin || {};
      const style = { margin: `${mar.t||0}px ${mar.r||0}px ${mar.b||0}px ${mar.l||0}px` };
      if (ex.imgWidth > 0) style.width = ex.imgWidth + 'px';
      if (ex.imgHeight > 0) style.height = ex.imgHeight + 'px';
      if (ex.imgRadius > 0) style.borderRadius = ex.imgRadius + 'px';
      return style;
    },
    catTextStyle(node) {
      const ex = node.props.styleEx || {};
      const mar = ex.textMargin || {};
      const style = {};
      if (ex.textSize > 0) style.fontSize = ex.textSize + 'px';
      if (mar.t || mar.r || mar.b || mar.l) style.margin = `${mar.t||0}px ${mar.r||0}px ${mar.b||0}px ${mar.l||0}px`;
      return style;
    },
    tabLabelStyle(node, t, isActive) {
      const ex = node.props.styleEx || {};
      const kind = this.tabStyleKind(node);
      const pad = ex.tabPadding || {};
      const style = {};
      if (t.bgColor && kind === 'card') style.background = t.bgColor;
      if (t.textColor) style.color = t.textColor;
      if (ex.textSize > 0) style.fontSize = ex.textSize + 'px';
      if (pad.t || pad.r || pad.b || pad.l) {
        style.padding = `${pad.t || 0}px ${pad.r || 0}px ${pad.b || 0}px ${pad.l || 0}px`;
      }
      const ulWidth = Math.max(parseInt(ex.activeUnderlineWidth, 10) || 2, 1);
      if (kind === 'underline') {
        style.background = 'transparent';
        style.border = 'none';
        style.borderRadius = '0';
        if (isActive) {
          if (ex.activeColor) style.color = ex.activeColor;
          const ulColor = ex.activeUnderlineColor || ex.activeColor || 'var(--theme-primary, #4d6bfe)';
          style.borderBottom = `${ulWidth}px solid ${ulColor}`;
          style.fontWeight = '600';
        } else {
          style.borderBottom = `${ulWidth}px solid transparent`;
        }
      } else if (isActive) {
        if (ex.activeBg) style.background = ex.activeBg;
        if (ex.activeColor) style.color = ex.activeColor;
        if (ex.tabRadius > 0) style.borderRadius = ex.tabRadius + 'px';
        style.fontWeight = '600';
      } else if (ex.tabRadius > 0) {
        style.borderRadius = ex.tabRadius + 'px';
      }
      return style;
    },
    tabStyleKind(node) {
      const ex = (node.props && node.props.styleEx) || {};
      return ex.style === 'underline' ? 'underline' : 'card';
    },
    navIconImgStyle(node, it) {
      const ex = node.props.styleEx || {};
      const pad = ex.imgPadding || {};
      const mar = ex.imgMargin || {};
      const radius = it.imgRadius != null && it.imgRadius !== '' ? it.imgRadius : (ex.imgRadius || 0);
      const style = {
        borderRadius: radius + 'px',
        padding: `${pad.t || 0}px ${pad.r || 0}px ${pad.b || 0}px ${pad.l || 0}px`,
        margin: `${mar.t || 0}px ${mar.r || 0}px ${mar.b || 0}px ${mar.l || 0}px`,
        boxSizing: 'border-box'
      };
      if (ex.imgWidth > 0) style.width = ex.imgWidth + 'px';
      else style.width = '150px';
      if (ex.imgHeight > 0) style.height = ex.imgHeight + 'px';
      else style.height = '150px';
      return style;
    },
    navIconTextStyle(node, it) {
      const ex = node.props.styleEx || {};
      const mar = ex.textMargin || {};
      const style = {};
      const color = it.textColor || ex.textColor;
      if (color) style.color = color;
      if (ex.textSize > 0) style.fontSize = ex.textSize + 'px';
      if (mar.t || mar.r || mar.b || mar.l) {
        style.margin = `${mar.t || 0}px ${mar.r || 0}px ${mar.b || 0}px ${mar.l || 0}px`;
      }
      return style;
    },
    multiContainerStyle(node) {
      return { gap: '0' };
    },
    multiImgStyle(node, it) {
      const ex = this.multiStyleEx(node);
      const pad = ex.imgPadding || {};
      const mar = ex.imgMargin || {};
      const radius = it.radius != null && it.radius !== '' ? it.radius : (ex.imgRadius || 0);
      const w = it.width > 0 ? it.width : (ex.imgWidth || 0);
      const h = it.height > 0 ? it.height : (ex.imgHeight || 0);
      const style = {
        borderRadius: radius + 'px',
        padding: `${pad.t || 0}px ${pad.r || 0}px ${pad.b || 0}px ${pad.l || 0}px`,
        margin: `${mar.t || 0}px ${mar.r || 0}px ${mar.b || 0}px ${mar.l || 0}px`,
        boxSizing: 'border-box'
      };
      if (w > 0) style.width = w + 'px';
      if (h > 0) style.height = h + 'px';
      return style;
    },
    imgScrollStyle(node) {
      const ex = node.props.styleEx || {};
      return {
        width: (ex.imgWidth || 286) + 'px',
        height: (ex.imgHeight || 272) + 'px',
        marginRight: (ex.gap || 20) + 'px'
      };
    },
    imgScrollItemStyle(node, it) {
      const ex = node.props.styleEx || {};
      return {
        width: (it.width > 0 ? it.width : (ex.imgWidth || 286)) + 'px',
        height: (it.height > 0 ? it.height : (ex.imgHeight || 272)) + 'px',
        marginRight: (ex.gap || 20) + 'px',
        borderRadius: (it.radius || 0) + 'px'
      };
    },
    /** richText XSS 过滤：DOMParser 惰性解析 + 危险标签/属性剥离（用户端渲染前最后一道防线） */
    safeHtml(html) {
      if (!html) return '';
      let doc;
      try {
        doc = new DOMParser().parseFromString(String(html), 'text/html');
      } catch (e) {
        return '';
      }
      const badTags = ['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'LINK', 'META', 'BASE', 'FORM', 'SVG'];
      const walk = node => {
        Array.prototype.slice.call(node.childNodes).forEach(child => {
          if (child.nodeType !== 1) return;
          if (badTags.indexOf(child.tagName) >= 0) { node.removeChild(child); return; }
          Array.prototype.slice.call(child.attributes).forEach(attr => {
            const name = attr.name.toLowerCase();
            const val = (attr.value || '').replace(/[\s\u0000-\u001f]/g, '').toLowerCase();
            if (name.indexOf('on') === 0) {
              child.removeAttribute(attr.name);
            } else if (/^(href|src|xlink:href|formaction)$/.test(name) && /^(javascript|vbscript):/.test(val)) {
              child.removeAttribute(attr.name);
            }
          });
          walk(child);
        });
      };
      walk(doc.body);
      return doc.body.innerHTML;
    },
    searchKeywordOf (node) {
      return (node && node.id && this.searchKeywords[node.id]) || '';
    },
    onSearchKeywordInput (node, e) {
      if (!node || !node.id) return;
      this.$set(this.searchKeywords, node.id, e.target.value);
    },
    submitSearchNav (node) {
      const kw = ((node && node.id && this.searchKeywords[node.id]) || '').trim();
      this.goSearchGoods(kw);
    },
    /** 跳转商品搜索页（keyword 需 JSON 字符串，与 header 一致；同页同词不重复导航） */
    goSearchGoods (kw) {
      const query = kw ? { keyword: JSON.stringify(kw) } : {};
      const path = '/searchGoods';
      const sameTarget = this.$route.path === path &&
        String(this.$route.query.keyword || '') === String(query.keyword || '');
      if (sameTarget) return;
      const nav = this.$route.path === path
        ? this.$router.replace({ path, query })
        : this.$router.push({ path, query });
      if (nav && typeof nav.catch === 'function') {
        nav.catch(err => { if (err && err.name !== 'NavigationDuplicated') throw err; });
      }
    },
    loadHotSearchWords () {
      hotSearchList().then(r => {
        const data = r && r.data && r.data.data;
        this.hotSearchWords = Array.isArray(data) ? data : [];
      }).catch(() => {});
    },
    /** 该搜索框节点是否展示热搜词（需开关开启且有热搜词） */
    showNodeHotSearch (node) {
      return node && node.props && node.props.showHotSearch === 'show' && this.hotSearchWords.length > 0;
    },
    goCart () {
      this.goLink({ linkType: 'systemPage', value: 'cart' });
    },
    goLink(link) {
      if (!link || !link.linkType || link.linkType === 'none' || !link.value) return;
      switch (link.linkType) {
        case 'goods':
          this.$router.push({ path: '/productDetails', query: { id: link.value } });
          break;
        case 'category':
          this.$router.push({ path: '/commodity', query: { id: link.value } });
          break;
        case 'customPage':
          this.$router.push({ path: '/page/' + link.value });
          break;
        case 'systemPage': {
          const path = SYSTEM_PAGE_PC[link.value];
          if (path) this.$router.push({ path });
          break;
        }
        case 'url':
          if (/^https?:\/\//.test(link.value)) window.open(link.value);
          break;
        default:
          break;
      }
    },
    /** 优惠券领取（与 couponCenterPage 同一 API） */
    claimCoupon(coupon) {
      if (!coupon || !coupon.id) return;
      getCoupon({ id: coupon.id }).then(r => {
        const ok = r && r.data && (r.data.status === 0 || r.data.code === 0 || !r.data.status);
        if (ok) {
          this.$message ? this.$message.success('领取成功') : alert('领取成功');
        } else {
          const msg = (r && r.data && r.data.message) || '领取失败';
          this.$message ? this.$message.warning(msg) : alert(msg);
        }
      }).catch(() => {
        this.$message ? this.$message.error('领取失败，请重试') : alert('领取失败');
      });
    },
    brandShowBorder (node) {
      const ex = (node.props && node.props.styleEx) || {};
      return (ex.showBorder || 'show') !== 'hide';
    },
    /** 品牌点击：跳商品列表（按 brandId 筛选） */
    goBrand(b) {
      if (!b || !b.id) return;
      this.$router.push({ path: '/commodity', query: { brandId: b.id } });
    },
    /** 店铺推荐点击：跳店铺详情 */
    goStore(s) {
      if (!s || !s.id) return;
      this.$router.push({ path: '/store', query: { storeId: s.id } });
    }
  }
};
</script>

<style scoped>
/* 阶段 5：根容器改 flex-wrap 支持 colSpan 横向并排 */
.decoration-renderer { display: flex; flex-wrap: wrap; align-items: flex-start; gap: 8px 0; }
.dr-node { overflow: hidden; box-sizing: border-box; }
/* 预览模式：给空组件最小高度兜底（防 0 高度看不到拖入位置）；非空节点跟随内容真实高度
   原 60px 太大，会强行撑高所有控件，让用户在 PropPanel 设的 height（如 20）"看起来不响应" */
.decoration-renderer--preview .dr-node {
  min-height: 20px;
  position: relative;
  outline: 1px dashed transparent;
  transition: outline-color .15s;
}
/* 但完全空（无任何子内容）的 dr-node 保留较大占位高度，避免拖入位置看不见 */
.decoration-renderer--preview .dr-node:empty { min-height: 40px; }
.decoration-renderer--preview .dr-node:hover { outline-color: #4d6bfe; }
/* 布局类组件（容器/辅助留白/Tab切换）：放开 overflow，让桥接附加的常驻 toolbar 可溢出显示 */
.decoration-renderer--preview .dr-node[data-node-type="container"],
.decoration-renderer--preview .dr-node[data-node-type="blank"],
.decoration-renderer--preview .dr-node[data-node-type="tabs"] {
  min-height: 0 !important;
  overflow: visible !important;
}
.dr-container { display: flex; flex-basis: 100%; max-width: 100%; }
.dr-col { flex: 1; min-width: 0; }
.dr-banner { width: 100%; }
.dr-banner__slide { position: relative; width: 100%; height: 100%; cursor: pointer; }
.dr-banner__img { width: 100%; height: 100%; object-fit: cover; display: block; }
.dr-banner__overlay { position: absolute; inset: 0; pointer-events: none; }
.dr-banner__title { position: absolute; bottom: 16px; left: 16px; right: 16px; font-size: 18px; font-weight: 600; text-shadow: 0 1px 3px rgba(0,0,0,.4); pointer-events: none; }
.dr-img { display: block; width: 100%; object-fit: cover; cursor: pointer; }
.dr-multi { display: flex; flex-wrap: wrap; }
.dr-multi__img { flex: 1; min-width: 120px; object-fit: cover; cursor: pointer; }
.dr-richtext { font-size: 14px; }
.dr-catnav__icon { object-fit: contain; }
.dr-catnav__inner { flex-shrink: 0; }
.dr-catnav__name { display: -webkit-box; max-width: 100%; overflow: hidden; text-overflow: ellipsis;
  white-space: normal; word-break: break-word; -webkit-box-orient: vertical; -webkit-line-clamp: 2; line-clamp: 2; }
.dr-goodscat__icon { width: 18px; height: 18px; object-fit: contain; vertical-align: middle; margin-right: 3px; }
.dr-video { width: 100%; display: block; }
.dr-placeholder { background: #f0f2f5; color: #999; text-align: center; padding: 40px 0; font-size: 13px; }
.dr-searchnav { display: flex; align-items: center; gap: 10px; padding: 8px 14px; }
.dr-searchnav__logo { font-weight: 700; }
.dr-searchnav__box { flex: 1; background: #f2f3f5; color: #999; font-size: 13px; padding: 8px 14px;
  display: flex; align-items: center; gap: 8px; }
.dr-searchnav__input { flex: 1; min-width: 0; border: 0; background: transparent; outline: none;
  font-size: 13px; color: #333; line-height: 1.4; }
.dr-searchnav__input::placeholder { color: #999; }
.dr-searchnav__cart { font-size: 18px; cursor: pointer; }
.dr-searchnav__cart-img { width: 24px; height: 24px; object-fit: contain; cursor: pointer; }
.dr-search { padding: 8px 14px; }
.dr-search__box { color: #999; font-size: 13px; padding: 8px 16px;
  display: flex; align-items: center; gap: 8px; }
/* 装修搜索框下方热搜词：水平居中 */
.dr-searchbox-wrap { display: flex; flex-direction: column; align-items: center; }
.dr-hotsearch {
  width: 100%; display: flex; flex-wrap: wrap; justify-content: center; align-items: center;
  gap: 16px; padding: 14px 14px 18px; font-size: 14px; box-sizing: border-box;
}
.dr-hotsearch__label { color: #999; font-weight: 500; }
.dr-hotsearch__word {
  color: #666; cursor: pointer; padding: 2px 10px; border-radius: 14px;
  background: #f5f5f5; transition: all .2s;
}
.dr-hotsearch__word:hover { color: #fff; background: var(--theme-primary, #4d6bfe); }
.dr-search__input { flex: 1; min-width: 0; border: 0; background: transparent; outline: none;
  font-size: 13px; color: #333; line-height: 1.4; }
.dr-search__input::placeholder { color: #999; }
.dr-search__placeholder { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.dr-search__btn { font-size: 16px; flex-shrink: 0; line-height: 1; }
.dr-search__btn-img { width: 20px; height: 20px; object-fit: contain; flex-shrink: 0; }
.dr-iconrow { display: grid; gap: 10px; padding: 12px; }
.dr-iconrow__item { display: flex; flex-direction: column; align-items: center; gap: 4px; cursor: pointer; font-size: 12px; }
.dr-iconrow__item img { object-fit: cover; }
.dr-catnav { padding: 0 14px; }
.dr-catnav__item { box-sizing: border-box; cursor: pointer; flex-shrink: 0; padding: 4px 6px; }
.dr-catnav__item--top { text-align: center; }
.dr-catnav__item--left { text-align: left; }
.dr-goodscat { display: flex; flex-wrap: wrap; gap: 12px; padding: 10px 14px; }
.dr-goodscat__item { font-size: 13px; cursor: pointer; }
.dr-imgscroll { display: flex; overflow-x: auto; padding: 10px 0 10px 12px; }
.dr-imgscroll img { object-fit: cover; flex-shrink: 0; cursor: pointer; }
.dr-sec-title { font-size: 16px; font-weight: 700; padding: 10px 14px; }
.dr-seckill-tabs { display: flex; gap: 10px; padding: 0 14px 10px; }
.dr-seckill-tab {
  flex: 1; max-width: 160px; text-align: center; padding: 8px 0;
  border-radius: 4px; background: #f5f5f5; cursor: pointer; color: #999;
}
.dr-seckill-tab .tab-time { font-size: 14px; font-weight: bold; }
.dr-seckill-tab .tab-name { font-size: 12px; }
.dr-seckill-tab.active { background: linear-gradient(90deg, #ee0a24, #ff6034); color: #fff; }
.dr-goods__grid { box-sizing: border-box; }
.dr-goods__card { width: 100%; min-width: 0; cursor: pointer; }
.dr-goods__img { width: 100%; aspect-ratio: 1 / 1; object-fit: cover; border-radius: 6px; display: block; }
.dr-goods__name { font-size: 13px; color: #333; margin-top: 4px; overflow: hidden;
  text-overflow: ellipsis; white-space: nowrap; }
.dr-goods__price { color: #ff4444; font-weight: 700; }
.dr-empty { color: #bbb; font-size: 13px; padding: 20px; }
.dr-coupons { display: flex; flex-wrap: wrap; gap: 10px; padding: 12px; }
.dr-coupon { background: #fff; border: 1px solid #ed1c24; border-radius: 6px;
  padding: 12px 18px; text-align: center; min-width: 120px; }
.dr-coupon__amt { font-size: 22px; font-weight: 700; color: #ed1c24; }
.dr-coupon__name { font-size: 12px; color: #606266; margin: 4px 0 8px; }
.dr-coupon__btn { background: #ed1c24; color: #fff; font-size: 12px;
  padding: 4px 14px; border-radius: 999px; border: 0; cursor: pointer;
  transition: opacity .15s; }
.dr-coupon__btn:hover { opacity: .85; }
.dr-brands { padding: 12px; }
.dr-brands__grid { display: flex; flex-wrap: wrap; }
.dr-brand__logo { height: 80px; margin: 6px 0; padding: 0 6px; object-fit: contain;
  cursor: pointer; transition: transform .15s, border-color .15s; box-sizing: border-box; }
.dr-brand__logo--bordered { border: 1px solid #eee; }
.dr-brand__logo--bordered:hover { transform: scale(1.05); border-color: #4d6bfe; }
.dr-stores { padding: 12px; }
.dr-stores__grid { display: flex; flex-wrap: wrap; }
.dr-store { display: flex; align-items: center; gap: 6px; font-size: 13px;
  cursor: pointer; padding: 6px 10px; border-radius: 4px; transition: background .15s; box-sizing: border-box; }
.dr-store:hover { background: #f5f7ff; }
.dr-store__logo { width: 40px; height: 40px; object-fit: cover; border-radius: 50%; flex-shrink: 0; }
.dr-hot { padding: 0; }
.dr-hot__grid { box-sizing: border-box; }
.dr-hot__item { display: flex; flex-direction: column; align-items: center; cursor: pointer; font-size: 12px; min-width: 0; width: 100%; }
.dr-hot__item img { width: 100%; aspect-ratio: 1 / 1; object-fit: cover; display: block; }
.dr-tabs__bar { display: flex; gap: 16px; padding: 10px 14px; flex-wrap: wrap; align-items: flex-end; }
.dr-tabs--underline .dr-tabs__bar { border-bottom: 1px solid #eee; }
.dr-tabs__label { font-size: 14px; color: #666; cursor: pointer; box-sizing: border-box; position: relative; }
/* Tab 角标：明确锚定到 tab 标签右上角外侧 */
.dr-tabs__badge { position: absolute; top: -8px; right: -10px;
  min-width: 16px; height: 16px; padding: 0 5px; box-sizing: border-box;
  font-size: 10px; line-height: 16px; color: #fff; background: #ff4d4f;
  border-radius: 8px; text-align: center; white-space: nowrap; font-weight: 500;
  pointer-events: none; z-index: 1; }
.dr-tabs--card .dr-tabs__label { border-radius: 4px; }
.dr-tabs--card .dr-tabs__label.is-active { font-weight: 600; }
.dr-tabs--underline .dr-tabs__label.is-active { font-weight: 600; }
/* Tab 内容区最小高度，确保空 tab 在 iframe 装修时有可拖入面积 */
.dr-tabs > .decoration-renderer { min-height: 80px; }
/* ===== 装修主题 token 接入：受 admin 主题切换控制 ===== */
.dr-goods__price { color: var(--theme-danger, #ff4444); }
.dr-coupon { background: var(--theme-danger, #ed1c24); }
.dr-catnav__item { background: var(--theme-bg-page, #f2f3f5);
  border-radius: var(--theme-radius-base, 14px); }
.dr-searchnav__box { background: var(--theme-bg-page, #f2f3f5);
  border-radius: var(--theme-radius-base, 4px); }
.dr-search__box { background: var(--theme-bg-card, #ffffff);
  border-radius: var(--theme-radius-base, 4px); }
.dr-empty { color: var(--theme-text-secondary, #bbb); }
/* ===== 页脚/页头组件 ===== */
.dr-fglinks { display: flex; flex-direction: column; gap: 6px; padding: 0 12px; }
.dr-fglinks__title { font-weight: 600; margin-bottom: 12px; font-size: 14px; }
.dr-fglinks__item { font-size: 13px; cursor: pointer; text-decoration: none; transition: color .15s; }
.dr-fglinks__item:hover { color: var(--theme-primary, #4d6bfe); }
.dr-partners { display: flex; flex-wrap: wrap; gap: 24px; align-items: center;
  padding: 20px 0; justify-content: center; }
.dr-partner { display: flex; align-items: center; cursor: pointer; }
.dr-partner img { display: block; object-fit: contain; opacity: .85; transition: opacity .15s; }
.dr-partner:hover img { opacity: 1; }
.dr-qrblock { display: flex; gap: 24px; align-items: flex-start; padding: 12px; flex-wrap: wrap; }
.dr-qrblock__item { display: flex; flex-direction: column; align-items: center; gap: 6px; }
.dr-qrblock__img { width: 96px; height: 96px; object-fit: cover; border: 1px solid #ebeef5; }
.dr-qrblock__label { font-size: 12px; color: var(--theme-text-secondary, #909399); }
.dr-qrblock__tip { color: var(--theme-danger, #ff4444); font-size: 13px; align-self: center; }
.dr-icp { text-align: center; padding: 12px 0; font-size: 12px; border-top: 1px solid #2a2a2a; }
.dr-icp a { text-decoration: none; }
.dr-topnav { display: flex; align-items: center; padding: 12px 0; flex-wrap: wrap; }
.dr-topnav__item { font-size: 14px; cursor: pointer; text-decoration: none; transition: color .15s; }
.dr-topnav__item:hover { color: var(--theme-primary, #4d6bfe); }
</style>
